import { UNVIEWED_ORDERS_TTL_MS } from '@/constants/socketTimings';
import { isRecord } from '@/utils/isRecord';

export const UNVIEWED_ORDERS_KEY = 'mercureUnviewedOrders';

type TUnviewedOrdersMap = Record<string, number>;

const isUnviewedOrdersMap = (value: unknown): value is TUnviewedOrdersMap =>
  isRecord(value) &&
  Object.values(value).every((savedAt) => typeof savedAt === 'number');

const readMap = (): TUnviewedOrdersMap => {
  const raw = localStorage.getItem(UNVIEWED_ORDERS_KEY);

  if (raw === null) {
    return {};
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (!isUnviewedOrdersMap(parsed)) {
    return {};
  }

  return parsed;
};

const writeMap = (map: TUnviewedOrdersMap): void => {
  localStorage.setItem(UNVIEWED_ORDERS_KEY, JSON.stringify(map));
};

const dropExpired = (map: TUnviewedOrdersMap): TUnviewedOrdersMap => {
  const now = Date.now();
  const fresh: TUnviewedOrdersMap = {};

  Object.entries(map).forEach(([uuid, savedAt]) => {
    if (now - savedAt <= UNVIEWED_ORDERS_TTL_MS) {
      fresh[uuid] = savedAt;
    }
  });

  return fresh;
};

/**
 * Помечает заказ как новый непросмотренный (для подсветки строки в таблице).
 * Хранится в localStorage с меткой времени, переживает F5.
 *
 * @param uuid - uuid заказа из события Mercure
 */
export const markOrderUnviewed = (uuid: string): void => {
  const map = dropExpired(readMap());
  map[uuid] = Date.now();
  writeMap(map);
};

/**
 * Снимает пометку «непросмотренный» с заказа. Вызывается при первом
 * открытии страницы заказа.
 *
 * @param uuid - uuid заказа
 */
export const markOrderViewed = (uuid: string): void => {
  const map = dropExpired(readMap());

  if (!(uuid in map)) {
    return;
  }

  const next: TUnviewedOrdersMap = {};
  Object.entries(map).forEach(([key, savedAt]) => {
    if (key !== uuid) {
      next[key] = savedAt;
    }
  });

  writeMap(next);
};

/**
 * Возвращает список uuid непросмотренных заказов, у которых не истёк TTL.
 * Просроченные записи попутно удаляются из хранилища.
 *
 * @returns массив uuid непросмотренных заказов
 */
export const getUnviewedOrderUuids = (): string[] => {
  const fresh = dropExpired(readMap());
  writeMap(fresh);
  return Object.keys(fresh);
};

/**
 * Полностью очищает реестр непросмотренных заказов. Вызывается при логауте,
 * чтобы после повторного входа подсветка не оставалась.
 */
export const clearUnviewedOrders = (): void => {
  localStorage.removeItem(UNVIEWED_ORDERS_KEY);
};
